import { Link } from "react-router-dom";

const featureCards = [
  {
    icon: "ri-search-line",
    title: "Find Products",
    desc: "Discover organized product and category resources from the Basetao Spreadsheet.",
  },
  {
    icon: "ri-scales-3-line",
    title: "Compare Options",
    desc: "Compare products, shipping routes and research factors side by side before ordering.",
  },
  {
    icon: "ri-image-line",
    title: "Check QC",
    desc: "Learn how to inspect QC photos, measurements and product details before shipping.",
  },
  {
    icon: "ri-ship-line",
    title: "Plan Shipping",
    desc: "Understand shipping costs, routes and important considerations for your haul.",
  },
];

const steps = [
  { title: "Find", desc: "Browse products and categories in the spreadsheet." },
  { title: "Research", desc: "Review available information and product details." },
  { title: "Check QC", desc: "Inspect QC photos and measurements carefully." },
  { title: "Compare", desc: "Compare products and shipping options." },
  { title: "Decide", desc: "Confirm after verifying current information." },
];

export default function GuideSections() {
  return (
    <>
      {/* Why Basetao Spreadsheet */}
      <section className="w-full bg-background-100 py-16 md:py-24">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <div className="flex flex-col items-center text-center">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">
              Why Basetao Spreadsheet
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-4xl">
              Your Complete Basetao Spreadsheet Research Hub
            </h2>
            <p className="mt-5 max-w-3xl text-base leading-relaxed text-foreground-600">
              Searching for products one by one can be slow and confusing. A well-organized Basetao
              Spreadsheet makes product discovery, comparison and research much easier. Explore
              product resources, understand QC photos, compare shipping options and make
              better-informed decisions before ordering.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {featureCards.map((card) => (
              <div
                key={card.title}
                className="rounded-lg border border-background-200 bg-background-50 p-6 transition-colors hover:border-primary-300"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${card.icon} text-lg`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                  {card.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="w-full bg-background-50 py-16 md:py-24">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <div className="flex flex-col items-center text-center">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">
              How It Works
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-4xl">
              A Simple, Practical Research Workflow
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {steps.map((step, index) => (
              <div
                key={step.title}
                className="relative rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-500 text-sm font-bold text-background-50">
                  {index + 1}
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                  {step.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QC section */}
      <section id="qc" className="w-full bg-background-100 py-16 md:py-24">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">
                QC Guide
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
                Check Before You Ship
              </h2>
              <p className="mt-4 text-base leading-relaxed text-foreground-600">
                QC photos are one of the most valuable parts of the Basetao workflow. Learning how
                to evaluate QC photos, measurements and product details before international
                shipping can help you catch problems early and avoid surprises.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  "Review QC photos against the original listing",
                  "Check measurements, colors and stitching details",
                  "Ask for extra photos when something looks unclear",
                  "Confirm the item matches your order before shipping",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-foreground-700">
                    <i className="ri-checkbox-circle-line mt-0.5 text-primary-600" aria-hidden="true"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="overflow-hidden rounded-lg">
              <img
                src="https://readdy.ai/api/search-image?query=A%20minimal%20flat%20lay%20of%20product%20quality%20control%20inspection%20tools%20with%20a%20magnifying%20glass%20and%20measurement%20ruler%20on%20a%20soft%20warm%20off-white%20surface%2C%20gentle%20natural%20lighting%2C%20clean%20editorial%20style%2C%20high%20detail%2C%20balanced%20composition&width=1200&height=800&seq=basetao-qc-01&orientation=landscape"
                alt="Basetao QC inspection guide"
                title="Basetao QC quality check"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Shipping section */}
      <section id="shipping" className="w-full bg-background-50 py-16 md:py-24">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div className="order-2 overflow-hidden rounded-lg lg:order-1">
              <img
                src="https://readdy.ai/api/search-image?query=A%20minimal%20scene%20of%20cardboard%20shipping%20boxes%20and%20a%20small%20parcel%20on%20a%20soft%20warm%20off-white%20surface%2C%20gentle%20natural%20lighting%2C%20clean%20editorial%20style%2C%20high%20detail%2C%20balanced%20composition&width=1200&height=800&seq=basetao-shipping-01&orientation=landscape"
                alt="Basetao shipping cost guide"
                title="Basetao shipping options"
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">
                Shipping Guide
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
                Understand Basetao Shipping Cost &amp; Options
              </h2>
              <p className="mt-4 text-base leading-relaxed text-foreground-600">
                Shipping can significantly affect your final order cost. Different routes, weights
                and destinations all play a role, so it helps to understand the main factors before
                you submit a parcel.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  "Compare shipping lines and estimated delivery times",
                  "Watch how package weight affects the final cost",
                  "Consider customs and import rules for your country",
                  "Confirm addresses and parcel details before shipping",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-foreground-700">
                    <i className="ri-checkbox-circle-line mt-0.5 text-primary-600" aria-hidden="true"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Safety / Trust section */}
      <section id="safety" className="w-full bg-primary-50 py-16 md:py-24">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <div className="flex flex-col items-center text-center">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">
              Trust &amp; Safety
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-4xl">
              Independent, Practical &amp; Safety-Focused
            </h2>
            <p className="mt-5 max-w-3xl text-base leading-relaxed text-foreground-700">
              This website is an independent information resource focused on research, transparency
              and buyer education. Always verify current prices, shipping rules, seller information
              and purchase conditions before ordering.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/basetao-spreadsheet-guide"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                Read the Basetao Spreadsheet Guide
                <i className="ri-arrow-right-line text-base" aria-hidden="true"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}